import { formComponents as fc } from "@/components/form";
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import usePatientMutation from "@/features/profile/api/usePatientMutation";
import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import type React from "react";
import { useRef } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import * as z from "zod";

const formSchema = z.object({
	day: z
		.number()
		.min(1, "Day is required")
		.max(31, "Max 31 days in a month")
		.optional(),
	month: z
		.number()
		.min(1, "Month is required")
		.max(12, "Only 12 months in a year")
		.optional(),
	year: z
		.number()
		.min(1900, "You're not that old!")
		.max(dayjs().year(), "That is the future!")
		.optional(),
});

interface Props {
	userId: string;
	dateOfBirth: string | null;
	onSuccess?: () => void;
	isDialog?: boolean;
}

const DateOfBirthForm = (props: Props) => {
	type FormSchemaType = z.infer<typeof formSchema>;

	const dayRef = useRef<HTMLInputElement>(null);
	const monthRef = useRef<HTMLInputElement>(null);
	const yearRef = useRef<HTMLInputElement>(null);

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			day: props.dateOfBirth ? dayjs(props.dateOfBirth).date() : undefined,
			month: props.dateOfBirth
				? dayjs(props.dateOfBirth).month() + 1
				: undefined,
			year: props.dateOfBirth ? dayjs(props.dateOfBirth).year() : undefined,
		},
	});

	const patientMutation = usePatientMutation(props.userId);

	const onSubmit: SubmitHandler<FormSchemaType> = async (formData) => {
		if (!formData.day || !formData.month || !formData.year) {
			return;
		}

		// Create date using UTC to avoid timezone issues
		const date_of_birth = new Date(
			Date.UTC(formData.year, formData.month - 1, formData.day),
		).toISOString();

		await patientMutation.mutate(
			{ date_of_birth },
			{ onSuccess: props.onSuccess },
		);
	};

	const handleInputChange = (
		event: React.ChangeEvent<HTMLInputElement>,
		field: { onChange: (value: number | undefined) => void },
		maxLength: number,
		nextRef?: React.RefObject<HTMLInputElement>,
	) => {
		let value = event.target.value;

		// Only allow numeric input from the start
		if (!/^\d*$/.test(value)) {
			// Find the last valid numeric string if invalid characters were added (e.g. paste)
			const match = value.match(/^(\d*)/);
			value = match ? match[0] : "";
		}

		// Ensure value does not exceed maxLength
		if (value.length > maxLength) {
			value = value.slice(0, maxLength);
		}

		if (value === "") {
			field.onChange(undefined);
		} else {
			// Convert to number if not empty
			field.onChange(Number(value));
		}

		// Auto-tab to next field if max length is reached
		if (value.length === maxLength && nextRef?.current) {
			nextRef.current.focus();
			nextRef.current.select();
		}
	};

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="form">
				<div className="flex flex-col gap-y-3">
					<FormLabel>Date of Birth</FormLabel>
					<div className="flex gap-x-2">
						<div className="flex flex-[1_1_0]">
							<FormField
								control={form.control}
								name="day"
								render={({ field }) => (
									<FormItem>
										<FormControl>
											<Input
												{...field}
												ref={dayRef}
												type="text"
												inputMode="numeric"
												placeholder="DD"
												value={field.value?.toString() || ""}
												onChange={(event) =>
													handleInputChange(event, field, 2, monthRef)
												}
												maxLength={2}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="flex flex-[1_1_0]">
							<FormField
								control={form.control}
								name="month"
								render={({ field }) => (
									<FormItem>
										<FormControl>
											<Input
												{...field}
												ref={monthRef}
												type="text"
												inputMode="numeric"
												placeholder="MM"
												value={field.value?.toString() || ""}
												onChange={(event) =>
													handleInputChange(event, field, 2, yearRef)
												}
												// maxLength={2}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="flex flex-[2_1_0]">
							<FormField
								control={form.control}
								name="year"
								render={({ field }) => (
									<FormItem>
										<FormControl>
											<Input
												{...field}
												ref={yearRef}
												type="text"
												inputMode="numeric"
												placeholder="YYYY"
												value={field.value?.toString() || ""}
												onChange={(event) => handleInputChange(event, field, 4)}
												maxLength={4}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>
					<FormDescription>Please use DD MM YYYY format.</FormDescription>
				</div>
				{props.isDialog ? (
					<fc.SaveButton isDialog={props.isDialog} />
				) : (
					<fc.NextButton />
				)}
			</form>
		</Form>
	);
};

export default DateOfBirthForm;
